import { useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material'
import { OpenInNew, Download, Refresh } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { fetchAllAttachments, type AttachmentSettingsRow } from '../../api/attachment-settings'
import { useApi } from '../../api/client'
import { downloadAttachment } from '../../api/attachments'
import { formatDatetimeInTimezone } from '../../utils/timezones'
import { useAuthStore } from '../../stores/auth.store'
import { useToastStore } from '../../stores/toast.store'

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function AttachmentManagement() {
  const timezone = useAuthStore((s) => s.user?.timezone) ?? 'UTC'
  const navigate = useNavigate()
  const toast = useToastStore()
  const { data: objectsData } = useApi<{ data: Array<{ id: string; name: string; label: string }> }>('/objects')
  const objects = objectsData?.data ?? []

  const [attachments, setAttachments] = useState<AttachmentSettingsRow[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [cursor, setCursor] = useState<string | null>(null)
  const [filterObjectId, setFilterObjectId] = useState('')

  const loadAttachments = useCallback(async (cursorParam?: string, objectId?: string) => {
    setLoading(true)
    try {
      const result = await fetchAllAttachments({
        cursor: cursorParam || undefined,
        limit: 20,
        object_id: objectId || undefined,
      })
      const newAttachments = result.data ?? []
      setAttachments((prev) => cursorParam ? [...prev, ...newAttachments] : newAttachments)
      setHasMore(result.pagination?.has_more ?? false)
      setCursor(result.pagination?.next_cursor ?? null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '加载附件列表失败')
    } finally {
      setLoading(false)
    }
  }, [])

  const handleFilterChange = useCallback((objectId: string) => {
    setFilterObjectId(objectId)
    setCursor(null)
    loadAttachments(undefined, objectId || undefined)
  }, [loadAttachments])

  const loadMore = useCallback(() => {
    if (cursor) loadAttachments(cursor, filterObjectId || undefined)
  }, [cursor, filterObjectId, loadAttachments])

  const handleNavigateToRecord = useCallback((objectId: string, recordId: string) => {
    navigate(`/o/${objectId}/${recordId}`)
  }, [navigate])

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Attachments</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by Object</InputLabel>
            <Select value={filterObjectId} label="Filter by Object" onChange={(e) => handleFilterChange(e.target.value)}>
              <MenuItem value="">All Objects</MenuItem>
              {objects.map((obj) => (
                <MenuItem key={obj.id} value={obj.id}>{obj.label || obj.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={() => loadAttachments(undefined, filterObjectId || undefined)} disabled={loading}>
            {attachments.length === 0 ? 'Load' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {attachments.length === 0 && !loading && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.secondary' }}>Click "Load" to view all attachments</Typography>
        </Paper>
      )}

      {attachments.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>File Name</TableCell>
                <TableCell>Object</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Uploaded By</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attachments.map((att) => (
                <TableRow key={att.id} hover>
                  <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {att.file_name}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={att.object_label || att.object_name || att.object_id}
                      size="small"
                      variant="outlined"
                      clickable
                      onClick={() => handleNavigateToRecord(att.object_id, att.record_id)}
                    />
                  </TableCell>
                  <TableCell>{formatFileSize(att.file_size)}</TableCell>
                  <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {att.content_type}
                  </TableCell>
                  <TableCell>{att.uploaded_by_name || '-'}</TableCell>
                  <TableCell>{formatDatetimeInTimezone(att.created_at, timezone)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Download">
                      <IconButton size="small" onClick={() => downloadAttachment(att.id, att.file_name)}>
                        <Download fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Go to Record">
                      <IconButton size="small" onClick={() => handleNavigateToRecord(att.object_id, att.record_id)}>
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {hasMore && !loading && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button size="small" onClick={loadMore}>Load More</Button>
        </Box>
      )}
    </Box>
  )
}
